import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Mensagem } from '../../services/mensagemService';

interface WhatsappViewMsgProps {
  open: boolean;
  onClose: () => void;
  mensagem: Mensagem | null;
}

const WhatsappViewMsg: React.FC<WhatsappViewMsgProps> = ({
  open,
  onClose,
  mensagem,
}) => {
  const formatWhatsAppDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isYesterday = date.toDateString() === new Date(now.getTime() - 86400000).toDateString();
      
      if (isToday) {
        return format(date, 'HH:mm', { locale: ptBR });
      } else if (isYesterday) {
        return `Ontem ${format(date, 'HH:mm', { locale: ptBR })}`;
      } else {
        return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
      }
    } catch {
      return dateString;
    }
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      enviado: 'Enviado',
      aguardando_envio: 'Aguardando Envio',
      falhou: 'Falhou',
    };
    return statusMap[status] || status;
  };

  if (!mensagem || mensagem.tipo !== 'whatsapp') {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: 0,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header do WhatsApp */}
      <Box
        sx={{
          bgcolor: '#075E54',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {mensagem.cliente?.nome || mensagem.destino}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {mensagem.destino}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Área de Mensagens com Background */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'auto',
          bgcolor: '#ECE5DD',
          backgroundImage: 'url(/fundo_zap.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: 'calc(90vh - 140px)',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Mensagem */}
        <Box
          sx={{
            alignSelf: 'flex-end',
            maxWidth: '75%',
            bgcolor: '#DCF8C6',
            borderRadius: '8px 8px 0 8px',
            p: 1.5,
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            position: 'relative',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#000',
              fontSize: '0.95rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {mensagem.conteudo}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#667781',
                fontSize: '0.7rem',
              }}
            >
              {formatWhatsAppDate(mensagem.data_envio || mensagem.createdAt)}
            </Typography>
            {mensagem.status === 'enviado' && (
              <CheckCircle
                sx={{
                  fontSize: '0.9rem',
                  color: '#53BDEB',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Informações adicionais (se houver erro) */}
        {mensagem.erro_mensagem && (
          <Box
            sx={{
              alignSelf: 'center',
              maxWidth: '85%',
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              borderRadius: '8px',
              p: 1.5,
              border: '1px solid rgba(244, 67, 54, 0.3)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#d32f2f',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Error sx={{ fontSize: '1rem' }} />
              Erro: {mensagem.erro_mensagem}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer com informações */}
      <Box
        sx={{
          bgcolor: '#F0F0F0',
          p: 1.5,
          borderTop: '1px solid #E0E0E0',
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              Status: {formatStatus(mensagem.status)}
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="textSecondary">
              Tentativas: {mensagem.tentativas}
            </Typography>
          </Grid>
          {mensagem.usuario && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Enviado por: {mensagem.usuario.nome}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Dialog>
  );
};

export default WhatsappViewMsg;

